import { getMyPermissions } from '@/api/admin/sub-admin'

export const ROLES = {
  ADMIN: 'ADMIN',
  SUB_ADMIN: 'SUB_ADMIN',
  CENTER: 'CENTER'
}

export const PERMISSIONS = {
  USER_VIEW: 'user:view',
  USER_MANAGE: 'user:manage',
  ORDER_VIEW: 'order:view',
  ORDER_AUDIT: 'order:audit',
  WITHDRAW_VIEW: 'withdraw:view',
  WITHDRAW_AUDIT: 'withdraw:audit',
  PRODUCT_VIEW: 'product:view',
  PRODUCT_MANAGE: 'product:manage',
  TEAM_VIEW: 'team:view',
  TEAM_MANAGE: 'team:manage',
  FINANCE_VIEW: 'finance:view',
  FINANCE_MANAGE: 'finance:manage',
  RISK_VIEW: 'risk:view',
  RISK_MANAGE: 'risk:manage',
  SYSTEM_VIEW: 'system:view',
  SYSTEM_MANAGE: 'system:manage',
  EXPORT_DATA: 'export:data'
}

export const MENU_PERMISSIONS = {
  '/admin/dashboard': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/profit': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/commission': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/users': [ROLES.ADMIN, ROLES.SUB_ADMIN, ROLES.CENTER],
  '/admin/orders': [ROLES.ADMIN, ROLES.SUB_ADMIN, ROLES.CENTER],
  '/admin/orders/pending': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/withdraw/index': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/products': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/staff-apply': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/rosters': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/promotion': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/employee': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/arrears': [ROLES.ADMIN, ROLES.SUB_ADMIN, ROLES.CENTER],
  '/admin/logistics': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/banners': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/news': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/notification': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/accounts': [ROLES.ADMIN],
  '/admin/sub-admin': [ROLES.ADMIN],
  '/admin/operation-log': [ROLES.ADMIN],
  '/admin/config': [ROLES.ADMIN],
  '/admin/complaints': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/appointments': [ROLES.ADMIN, ROLES.SUB_ADMIN],
  '/admin/home-video': [ROLES.ADMIN, ROLES.SUB_ADMIN]
}

export const PERMISSION_MENU_MAP = {
  HOME: ['/admin/dashboard'],
  DATA_DISPLAY: ['/admin/dashboard'],
  INCOME_MANAGE: ['/admin/profit', '/admin/commission'],
  USER_MANAGE: ['/admin/users'],
  PRODUCT_MANAGE: ['/admin/products'],
  ORDER_MANAGE: ['/admin/orders', '/admin/orders/pending'],
  TEAM_MANAGE: ['/admin/myTeam', '/admin/staff-apply', '/admin/rosters', '/admin/promotion', '/admin/employee'],
  WITHDRAW_MANAGE: ['/admin/withdraw/index'],
  ARREARS_MANAGE: ['/admin/arrears'],
  COMPLAINT_MANAGE: ['/admin/complaints'],
  APPOINTMENT_MANAGE: ['/admin/appointments'],
  LOGISTICS_MANAGE: ['/admin/logistics'],
  NEWS_MANAGE: ['/admin/banners', '/admin/news', '/admin/home-video'],
  NOTIFICATION_MANAGE: ['/admin/notification'],
  PERMISSION_MANAGE: ['/admin/sub-admin'],
  SYSTEM_MANAGE: ['/admin/operation-log', '/admin/config'],
  EMPLOYEE_MANAGE: ['/admin/employee']
}

let currentPermissions = null
let isFetchingPermissions = false

export async function fetchMyPermissions() {
  if (isFetchingPermissions) {
    return currentPermissions || []
  }

  if (currentPermissions !== null) {
    return currentPermissions
  }

  isFetchingPermissions = true
  try {
    const res = await getMyPermissions()
    const data = res.data || {}
    const permissionsObj = data.permissions || {}
    const permissions = Object.keys(permissionsObj).filter(key => permissionsObj[key] === true)
    currentPermissions = permissions
    localStorage.setItem('adminPermissions', JSON.stringify(permissions))
    if (data.role) {
      localStorage.setItem('adminRole', data.role)
    }
    if (data.isSuperAdmin !== undefined) {
      localStorage.setItem('isSuperAdmin', data.isSuperAdmin ? 'true' : 'false')
    }
    return permissions
  } catch (error) {
    console.error('获取权限失败:', error)
    currentPermissions = JSON.parse(localStorage.getItem('adminPermissions') || '[]')
    return currentPermissions
  } finally {
    isFetchingPermissions = false
  }
}

export function getCurrentPermissions() {
  if (currentPermissions) {
    return currentPermissions
  }
  return JSON.parse(localStorage.getItem('adminPermissions') || '[]')
}

export function clearPermissionCache() {
  currentPermissions = null
  isFetchingPermissions = false
}

export function hasPermission(permission) {
  const role = localStorage.getItem('adminRole')
  const permissions = getCurrentPermissions()

  if (role === ROLES.ADMIN) {
    return true
  }

  if (role === ROLES.SUB_ADMIN) {
    return permissions.includes(permission)
  }

  if (role === ROLES.CENTER) {
    const viewPermissions = [
      PERMISSIONS.USER_VIEW,
      PERMISSIONS.ORDER_VIEW,
      PERMISSIONS.TEAM_VIEW,
      PERMISSIONS.RISK_VIEW
    ]
    return viewPermissions.includes(permission) && !permission.includes('manage') && !permission.includes('audit')
  }

  return false
}

export function hasRole(role) {
  const currentRole = localStorage.getItem('adminRole')
  return currentRole === role
}

export function isSuperAdmin() {
  return hasRole(ROLES.ADMIN)
}

export function isSubAdmin() {
  return hasRole(ROLES.SUB_ADMIN)
}

export function isServiceCenter() {
  return hasRole(ROLES.CENTER)
}

export function isMenuVisible(path) {
  const role = localStorage.getItem('adminRole')
  const permissions = getCurrentPermissions()

  if (role === ROLES.ADMIN) {
    return true
  }

  for (const [permKey, paths] of Object.entries(PERMISSION_MENU_MAP)) {
    if (paths.includes(path) && permissions.includes(permKey)) {
      return true
    }
  }

  return false
}

export function canExport() {
  const role = localStorage.getItem('adminRole')
  if (role === ROLES.CENTER) {
    return false
  }
  if (role === ROLES.SUB_ADMIN) {
    const permissions = getCurrentPermissions()
    return permissions.includes(PERMISSIONS.EXPORT_DATA)
  }
  return role === ROLES.ADMIN
}

export function getRoleName() {
  const role = localStorage.getItem('adminRole')
  const roleNames = {
    ADMIN: '超级管理员',
    SUB_ADMIN: '子管理员',
    CENTER: '服务中心',
    LEADER: '业务经理'
  }
  return roleNames[role] || '未知角色'
}
